import { PRODUCT_NAME } from "@kuroo/brand";

const elementName = "kuroo-concierge";

type ChatRole = "user" | "assistant";
type ChatMessage = { id: string; role: ChatRole; parts: Array<{ type: "text"; text: string }> };
type QuoteAppointment = {
  serviceId?: string;
  serviceName?: string;
  providerId?: string;
  providerName?: string;
  locationId?: string;
  locationName?: string;
  startAt?: string;
  endAt?: string;
  timezone?: string;
  slotKey?: string;
  durationMinutes?: number;
  persons?: number;
};
type QuoteRule = { label?: string; reason?: string };
type QuoteOccurrence = QuoteAppointment & {
  sequence: number;
  quoteId?: string;
  holdId?: string;
  requestedStartAt?: string;
  requestedEndAt?: string;
  amountCents?: number;
  totalCents?: number;
  taxCents?: number;
  currency?: string;
  status?: string;
  adjusted?: boolean;
  adjustmentReason?: string;
  adjustmentAccepted?: boolean;
  lines?: unknown[];
  appliedRules?: QuoteRule[];
};
type QuoteSnapshot = {
  id: string;
  proposalId?: string;
  kind?: string;
  targetKind?: string;
  currency?: string;
  totalCents: number;
  expiresAt: string;
  lines?: unknown[];
  appointment?: QuoteAppointment;
  occurrences?: QuoteOccurrence[];
  appliedRules?: QuoteRule[];
  dynamicAdjustmentCents?: number;
  adjusted?: boolean;
  adjustmentReason?: string;
  adjustmentAccepted?: boolean;
  consentRequired?: boolean;
};
type CheckoutSnapshot = { paymentId?: string; bookingId?: string; eventOrderId?: string; checkoutUrl?: string; status?: string; statusDetail?: string; bookingStatus?: string; eventOrderStatus?: string; amountCents?: number; currency?: string };
type ConciergeCustomer = { firstName: string; email: string; lastName?: string; phone?: string; consent?: boolean };
type ConciergePaymentMethod = "mercadopago" | "stripe" | "paypal" | "on_site";
type ConciergeAction =
  | { type: "confirm-proposal"; proposalId: string; customer: ConciergeCustomer; adjustmentAccepted?: boolean; paymentMethod?: ConciergePaymentMethod }
  | { type: "update-proposal"; proposalId: string; occurrences: Array<{ sequence: number; include?: boolean; slotKey?: string; startAt?: string; endAt?: string; providerId?: string | null; locationId?: string | null; adjustmentAccepted?: boolean }> };

function randomId(prefix: string): string {
  try { return `${prefix}-${crypto.randomUUID()}`; }
  catch { return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`; }
}

function money(cents: number, currency = "EUR", locale = "es-ES"): string {
  try { return new Intl.NumberFormat(locale, { style: "currency", currency }).format(cents / 100); }
  catch { return `${(cents / 100).toFixed(2)} ${currency}`; }
}

function textFromParts(parts: unknown): string {
  if (!Array.isArray(parts)) return "";
  return parts.filter((part): part is { type: "text"; text: string } => Boolean(part) && typeof part === "object" && (part as Record<string, unknown>).type === "text" && typeof (part as Record<string, unknown>).text === "string")
    .map((part) => part.text).join("");
}

function objectRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : undefined;
}

function firstString(records: Array<Record<string, unknown> | undefined>, ...keys: string[]): string | undefined {
  for (const record of records) {
    for (const key of keys) {
      if (typeof record?.[key] === "string" && record[key].trim()) return record[key].trim();
    }
  }
  return undefined;
}

function firstNumber(records: Array<Record<string, unknown> | undefined>, ...keys: string[]): number | undefined {
  for (const record of records) {
    for (const key of keys) {
      if (typeof record?.[key] === "number" && Number.isFinite(record[key])) return Math.max(0, Math.round(record[key]));
    }
  }
  return undefined;
}

function ruleSnapshots(value: unknown): QuoteRule[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const rules = value.flatMap((item) => {
    if (typeof item === "string" && item.trim()) return [{ label: item.trim() }];
    const record = objectRecord(item);
    if (!record) return [];
    const label = firstString([record], "label", "name");
    const reason = firstString([record], "reason", "description");
    return label || reason ? [{ ...(label ? { label } : {}), ...(reason ? { reason } : {}) }] : [];
  });
  return rules.length ? rules : undefined;
}

function proposalOccurrenceStatus(value: string | undefined): string | undefined {
  if (value === "draft") return "proposed";
  if (value === "cancelled") return "excluded";
  return value;
}

function formatAppointmentRange(appointment: QuoteAppointment, locale: string): string | undefined {
  if (!appointment.startAt) return undefined;
  const start = new Date(appointment.startAt);
  if (Number.isNaN(start.getTime())) return undefined;
  const dateOptions: Intl.DateTimeFormatOptions = { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23" };
  const timeOptions: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit", hourCycle: "h23" };
  const makeFormatter = (options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
    try { return new Intl.DateTimeFormat(locale, { ...options, ...(appointment.timezone ? { timeZone: appointment.timezone } : {}) }); }
    catch { return new Intl.DateTimeFormat(locale, options); }
  };
  const startText = makeFormatter(dateOptions).format(start);
  if (!appointment.endAt) return startText;
  const end = new Date(appointment.endAt);
  if (Number.isNaN(end.getTime())) return startText;
  return `${startText} · ${makeFormatter(timeOptions).format(end)}`;
}

function quoteFromValue(value: unknown, depth = 0): QuoteSnapshot | undefined {
  if (depth > 5 || !value || typeof value !== "object") return undefined;
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = quoteFromValue(item, depth + 1);
      if (found) return found;
    }
    return undefined;
  }
  const record = value as Record<string, unknown>;
  const candidate = objectRecord(record.quote) ?? objectRecord(record.proposal) ?? record;
  if (candidate) {
    const item = candidate;
    const id = typeof item.id === "string" ? item.id : typeof item.quoteId === "string" ? item.quoteId : typeof record.quoteId === "string" ? record.quoteId : typeof item.proposalId === "string" ? item.proposalId : typeof record.proposalId === "string" ? record.proposalId : undefined;
    const totalCents = typeof item.totalCents === "number" ? item.totalCents : typeof record.totalCents === "number" ? record.totalCents : undefined;
    const expiresAt = typeof item.expiresAt === "string" ? item.expiresAt : typeof record.expiresAt === "string" ? record.expiresAt : undefined;
    if (id && totalCents !== undefined && expiresAt) {
      const nestedAppointments = [
        item.appointment,
        record.appointment,
        item.slot,
        record.slot,
        item.selectedSlot,
        record.selectedSlot,
        item.details,
        record.details
      ].map(objectRecord);
      const line = (Array.isArray(item.lines) ? item.lines : Array.isArray(record.lines) ? record.lines : [])
        .map(objectRecord)
        .find((entry) => Boolean(entry));
      const sources = [item, ...nestedAppointments, record, line];
      const provider = objectRecord(item.provider) ?? objectRecord(record.provider);
      const location = objectRecord(item.location) ?? objectRecord(record.location);
      const serviceName = firstString(sources, "serviceName", "service_name", "serviceTitle", "serviceLabel") ?? firstString([line], "label", "name");
      const appointment: QuoteAppointment = {
        ...(firstString(sources, "serviceId", "service_id") ? { serviceId: firstString(sources, "serviceId", "service_id") } : {}),
        ...(serviceName ? { serviceName } : {}),
        ...(firstString([provider, ...sources], "providerId", "provider_id") ? { providerId: firstString([provider, ...sources], "providerId", "provider_id") } : {}),
        ...(firstString([provider, ...sources], "providerName", "provider_name", "professionalName", "doctorName", "displayName", "name") ? { providerName: firstString([provider, ...sources], "providerName", "provider_name", "professionalName", "doctorName", "displayName", "name") } : {}),
        ...(firstString([location, ...sources], "locationId", "location_id") ? { locationId: firstString([location, ...sources], "locationId", "location_id") } : {}),
        ...(firstString([location, ...sources], "locationName", "location_name", "venueName", "officeName", "displayName", "name") ? { locationName: firstString([location, ...sources], "locationName", "location_name", "venueName", "officeName", "displayName", "name") } : {}),
        ...(firstString(sources, "startAt", "startsAt", "start", "appointmentStartAt") ? { startAt: firstString(sources, "startAt", "startsAt", "start", "appointmentStartAt") } : {}),
        ...(firstString(sources, "endAt", "endsAt", "end", "appointmentEndAt") ? { endAt: firstString(sources, "endAt", "endsAt", "end", "appointmentEndAt") } : {}),
        ...(firstString(sources, "timezone", "timeZone") ? { timezone: firstString(sources, "timezone", "timeZone") } : {}),
        ...(firstString(sources, "slotKey", "slot_key") ? { slotKey: firstString(sources, "slotKey", "slot_key") } : {}),
        ...(firstNumber(sources, "durationMinutes", "duration_minutes", "duration") !== undefined ? { durationMinutes: firstNumber(sources, "durationMinutes", "duration_minutes", "duration") } : {}),
        ...(firstNumber(sources, "persons", "quantity") !== undefined ? { persons: firstNumber(sources, "persons", "quantity") } : {})
      };
      const quotePricing = objectRecord(item.pricing) ?? objectRecord(record.pricing);
      const appliedRules = ruleSnapshots(item.appliedRules ?? record.appliedRules ?? quotePricing?.appliedRules);
      const lines = Array.isArray(item.lines) ? item.lines : Array.isArray(record.lines) ? record.lines : Array.isArray(quotePricing?.lines) ? quotePricing.lines : undefined;
      const dynamicAdjustmentCents = firstNumber([item, record, quotePricing], "dynamicAdjustmentCents", "dynamic_adjustment_cents");
      const adjusted = item.adjusted === true || record.adjusted === true || (dynamicAdjustmentCents !== undefined && dynamicAdjustmentCents !== 0) || Boolean(appliedRules?.length);
      const adjustmentReason = firstString([item, record], "adjustmentReason", "adjustment_reason") ?? appliedRules?.map((rule) => rule.reason ?? rule.label).find((value): value is string => Boolean(value));
      const rawOccurrences = Array.isArray(item.occurrences) ? item.occurrences : Array.isArray(record.occurrences) ? record.occurrences : undefined;
      const occurrences = rawOccurrences?.flatMap((raw, index) => {
        const row = objectRecord(raw);
        if (!row) return [];
        const occurrencePricing = objectRecord(row.pricing);
        const sources = [row, objectRecord(row.appointment), objectRecord(row.slot), objectRecord(row.provider), objectRecord(row.location)];
        const occurrenceAppliedRules = ruleSnapshots(row.appliedRules ?? occurrencePricing?.appliedRules);
        const occurrenceDynamicAdjustment = firstNumber([row, occurrencePricing], "dynamicAdjustmentCents", "dynamic_adjustment_cents");
        const occurrenceAdjusted = row.adjusted === true || (occurrenceDynamicAdjustment !== undefined && occurrenceDynamicAdjustment !== 0) || Boolean(occurrenceAppliedRules?.length);
        const occurrenceAdjustmentReason = firstString(sources, "adjustmentReason", "adjustment_reason") ?? occurrenceAppliedRules?.map((rule) => rule.reason ?? rule.label).find((value): value is string => Boolean(value));
        const occurrence: QuoteOccurrence = {
          sequence: typeof row.sequence === "number" ? row.sequence : index,
          ...(firstString(sources, "quoteId", "id") ? { quoteId: firstString(sources, "quoteId", "id") } : {}),
          ...(firstString(sources, "holdId") ? { holdId: firstString(sources, "holdId") } : {}),
          ...(firstString(sources, "requestedStartAt", "requested_start_at") ? { requestedStartAt: firstString(sources, "requestedStartAt", "requested_start_at") } : {}),
          ...(firstString(sources, "requestedEndAt", "requested_end_at") ? { requestedEndAt: firstString(sources, "requestedEndAt", "requested_end_at") } : {}),
          ...(firstString(sources, "serviceId", "service_id") ? { serviceId: firstString(sources, "serviceId", "service_id") } : {}),
          ...(firstString(sources, "serviceName", "service_name", "serviceTitle", "serviceLabel") ? { serviceName: firstString(sources, "serviceName", "service_name", "serviceTitle", "serviceLabel") } : {}),
          ...(firstString(sources, "providerId", "provider_id") ? { providerId: firstString(sources, "providerId", "provider_id") } : {}),
          ...(firstString(sources, "providerName", "provider_name", "professionalName", "doctorName", "displayName", "name") ? { providerName: firstString(sources, "providerName", "provider_name", "professionalName", "doctorName", "displayName", "name") } : {}),
          ...(firstString(sources, "locationId", "location_id") ? { locationId: firstString(sources, "locationId", "location_id") } : {}),
          ...(firstString(sources, "locationName", "location_name", "venueName", "officeName", "displayName", "name") ? { locationName: firstString(sources, "locationName", "location_name", "venueName", "officeName", "displayName", "name") } : {}),
          ...(firstString(sources, "startAt", "startsAt", "start", "requestedStartAt") ? { startAt: firstString(sources, "startAt", "startsAt", "start", "requestedStartAt") } : {}),
          ...(firstString(sources, "endAt", "endsAt", "end", "requestedEndAt") ? { endAt: firstString(sources, "endAt", "endsAt", "end", "requestedEndAt") } : {}),
          ...(firstString(sources, "timezone", "timeZone") ? { timezone: firstString(sources, "timezone", "timeZone") } : {}),
          ...(firstString(sources, "slotKey", "slot_key") ? { slotKey: firstString(sources, "slotKey", "slot_key") } : {}),
          ...(firstNumber(sources, "durationMinutes", "duration_minutes", "duration") !== undefined ? { durationMinutes: firstNumber(sources, "durationMinutes", "duration_minutes", "duration") } : {}),
          ...(firstNumber(sources, "persons", "quantity") !== undefined ? { persons: firstNumber(sources, "persons", "quantity") } : {}),
          ...(firstNumber(sources, "amountCents", "amount_cents") !== undefined ? { amountCents: firstNumber(sources, "amountCents", "amount_cents") } : {}),
          ...(firstNumber(sources, "totalCents", "total_cents") !== undefined ? { totalCents: firstNumber(sources, "totalCents", "total_cents") } : {}),
          ...(firstNumber(sources, "taxCents", "tax_cents") !== undefined ? { taxCents: firstNumber(sources, "taxCents", "tax_cents") } : {}),
          ...(firstString(sources, "currency") ? { currency: firstString(sources, "currency") } : {}),
          ...(proposalOccurrenceStatus(firstString(sources, "status")) ? { status: proposalOccurrenceStatus(firstString(sources, "status")) } : {}),
          ...(occurrenceAdjusted ? { adjusted: true } : {}),
          ...(occurrenceAdjustmentReason ? { adjustmentReason: occurrenceAdjustmentReason } : {}),
          ...(row.adjustmentAccepted === true ? { adjustmentAccepted: true } : {}),
          ...(Array.isArray(row.lines) ? { lines: row.lines } : Array.isArray(occurrencePricing?.lines) ? { lines: occurrencePricing.lines } : {}),
          ...(occurrenceAppliedRules ? { appliedRules: occurrenceAppliedRules } : {})
        };
        return [occurrence];
      });
      return {
        id,
        proposalId: firstString([item, record], "proposalId") ?? id,
        kind: firstString([item, record], "kind", "targetKind"),
        targetKind: firstString([item, record], "targetKind", "kind"),
        currency: firstString([item, record], "currency"),
        totalCents: Math.max(0, Math.round(totalCents)),
        expiresAt,
        ...(lines ? { lines } : {}),
        ...(Object.keys(appointment).length ? { appointment } : {}),
        ...(occurrences?.length ? { occurrences } : {}),
        ...(appliedRules ? { appliedRules } : {}),
        ...(dynamicAdjustmentCents !== undefined ? { dynamicAdjustmentCents } : {}),
        ...(adjusted ? { adjusted: true } : {}),
        ...(adjustmentReason ? { adjustmentReason } : {}),
        ...(item.adjustmentAccepted === true || record.adjustmentAccepted === true ? { adjustmentAccepted: true } : {}),
        ...(record.consentRequired === true || item.consentRequired === true ? { consentRequired: true } : {})
      };
    }
  }
  for (const item of Object.values(record)) {
    const found = quoteFromValue(item, depth + 1);
    if (found) return found;
  }
  return undefined;
}

function secureUrl(value: unknown): string | undefined {
  if (typeof value !== "string" || value.length > 2_048) return undefined;
  try {
    const url = new URL(value);
    const local = url.protocol === "http:" && ["localhost", "127.0.0.1", "::1"].includes(url.hostname);
    if (url.protocol !== "https:" && !local) return undefined;
    if (url.username || url.password || url.hash) return undefined;
    return url.toString();
  } catch { return undefined; }
}

function checkoutFromValue(value: unknown, depth = 0): CheckoutSnapshot | undefined {
  if (depth > 5 || !value || typeof value !== "object") return undefined;
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = checkoutFromValue(item, depth + 1);
      if (found) return found;
    }
    return undefined;
  }
  const record = value as Record<string, unknown>;
  const payload = record.payload && typeof record.payload === "object" && !Array.isArray(record.payload) ? record.payload as Record<string, unknown> : record;
  const nestedCheckout = objectRecord(payload.checkout);
  const checkoutPayload = nestedCheckout ? { ...payload, ...nestedCheckout } : payload;
  const payment = objectRecord(checkoutPayload.payment);
  const paymentLink = objectRecord(checkoutPayload.paymentLink);
  const paymentId = typeof checkoutPayload.paymentId === "string" ? checkoutPayload.paymentId : typeof payment?.paymentId === "string" ? payment.paymentId : typeof payment?.id === "string" ? payment.id : undefined;
  const checkoutUrl = secureUrl(checkoutPayload.checkoutUrl) ?? secureUrl(payment?.checkoutUrl) ?? secureUrl(paymentLink?.url);
  if (paymentId || checkoutUrl || typeof checkoutPayload.bookingId === "string" || typeof checkoutPayload.eventOrderId === "string" || record.type === "checkout") {
    return {
      ...(paymentId ? { paymentId } : {}),
      ...(typeof checkoutPayload.bookingId === "string" ? { bookingId: checkoutPayload.bookingId } : {}),
      ...(typeof checkoutPayload.eventOrderId === "string" ? { eventOrderId: checkoutPayload.eventOrderId } : {}),
      ...(checkoutUrl ? { checkoutUrl } : {}),
      ...(typeof checkoutPayload.status === "string" ? { status: checkoutPayload.status } : typeof payment?.status === "string" ? { status: payment.status } : {}),
      ...(typeof checkoutPayload.statusDetail === "string" ? { statusDetail: checkoutPayload.statusDetail } : {}),
      ...(typeof checkoutPayload.bookingStatus === "string" ? { bookingStatus: checkoutPayload.bookingStatus } : {}),
      ...(typeof checkoutPayload.eventOrderStatus === "string" ? { eventOrderStatus: checkoutPayload.eventOrderStatus } : {}),
      ...(typeof checkoutPayload.amountCents === "number" ? { amountCents: checkoutPayload.amountCents } : typeof payment?.amountCents === "number" ? { amountCents: payment.amountCents } : {}),
      ...(typeof checkoutPayload.currency === "string" ? { currency: checkoutPayload.currency } : typeof payment?.currency === "string" ? { currency: payment.currency } : {})
    };
  }
  for (const item of Object.values(record)) {
    const found = checkoutFromValue(item, depth + 1);
    if (found) return found;
  }
  return undefined;
}

function eventFromValue(value: unknown, depth = 0): { kind: "quote" | "hold" | "checkout"; value: unknown } | undefined {
  if (depth > 5 || !value || typeof value !== "object") return undefined;
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = eventFromValue(item, depth + 1);
      if (found) return found;
    }
    return undefined;
  }
  const record = value as Record<string, unknown>;
  if (quoteFromValue(record)) return { kind: "quote", value: quoteFromValue(record) };
  if (typeof record.holdId === "string" && typeof record.expiresAt === "string") return { kind: "hold", value: record };
  if (typeof record.paymentId === "string" || (record.payment && typeof record.payment === "object") || (record.paymentLink && typeof record.paymentLink === "object")) return { kind: "checkout", value: record };
  for (const item of Object.values(record)) {
    const found = eventFromValue(item, depth + 1);
    if (found) return found;
  }
  return undefined;
}

function safeSessionName(): string {
  const key = "kuroo-concierge-session";
  try {
    const current = window.sessionStorage.getItem(key);
    if (current && /^[a-z0-9-]{12,100}$/i.test(current)) return current;
    const next = randomId("session").replace(/[^a-z0-9-]/gi, "").slice(0, 80);
    window.sessionStorage.setItem(key, next);
    return next;
  } catch { return randomId("session"); }
}

export class KurooConciergeElement extends HTMLElement {
  static get observedAttributes(): string[] {
    return ["api-base-url", "agent-base-url", "service-id", "provider-id", "location-id", "locale", "theme", "payment-method"];
  }

  private readonly root = this.attachShadow({ mode: "open" });
  private socket?: WebSocket;
  private readonly sessionName = safeSessionName();
  private messages: ChatMessage[] = [];
  private activeAssistant = "";
  private activeRequestId?: string;
  private queuedText?: string;
  private queuedAction?: ConciergeAction;
  private pendingQuote?: QuoteSnapshot;
  private holdExpiresAt?: string;
  private pendingCheckout?: CheckoutSnapshot;
  private customer: ConciergeCustomer = { firstName: "", email: "" };
  private paymentPollTimer?: number;
  private paymentPollAttempts = 0;
  private reconnectTimer?: number;
  private reconnectAttempts = 0;
  private resumeProbeId?: string;
  private readonly resumeAckedRequestIds = new Set<string>();
  private connectionWanted = false;

  connectedCallback(): void {
    this.render();
  }

  disconnectedCallback(): void {
    this.connectionWanted = false;
    this.resumeProbeId = undefined;
    this.resumeAckedRequestIds.clear();
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    this.socket?.close(1000, "widget disconnected");
    this.socket = undefined;
    if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
    this.paymentPollTimer = undefined;
  }

  attributeChangedCallback(): void {
    if (this.isConnected) this.render();
  }

  private currentLocale(): string {
    return this.getAttribute("locale") || document.documentElement.lang || navigator.language || "es-ES";
  }

  private agentUrl(): string | undefined {
    const base = this.getAttribute("agent-base-url")?.trim();
    if (!base) return undefined;
    try {
      const url = new URL(base, document.baseURI);
      url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
      url.pathname = `${url.pathname.replace(/\/$/, "")}/agents/concierge-agent/${encodeURIComponent(this.sessionName)}`;
      return url.toString();
    } catch { return undefined; }
  }

  private context(): Record<string, unknown> {
    const value: Record<string, unknown> = { locale: this.currentLocale() };
    for (const name of ["service-id", "provider-id", "location-id"] as const) {
      const attribute = name.replaceAll("-", "");
      const current = this.getAttribute(name);
      if (current) value[attribute === "serviceid" ? "serviceId" : attribute === "providerid" ? "providerId" : "locationId"] = current;
    }
    const paymentMethod = this.getAttribute("payment-method")?.trim();
    if (paymentMethod && ["mercadopago", "stripe", "paypal", "on_site"].includes(paymentMethod)) value.paymentMethod = paymentMethod;
    return value;
  }

  private selectedPaymentMethod(): ConciergePaymentMethod | undefined {
    const value = this.getAttribute("payment-method")?.trim();
    return value === "mercadopago" || value === "stripe" || value === "paypal" || value === "on_site" ? value : undefined;
  }

  private render(): void {
    const theme = this.getAttribute("theme") || "default";
    const hasUserMessage = this.messages.some((message) => message.role === "user");
    const inputPlaceholder = hasUserMessage ? "" : "Ej.: necesito un turno esta semana…";
    this.root.innerHTML = `<style>
      :host { display:block; color:#26343b; font:15px/1.45 system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif; }
      .shell { border:1px solid #dbe3e7; border-radius:16px; background:#fff; box-shadow:0 12px 36px rgba(25,45,55,.12); overflow:hidden; max-width:680px; }
      header { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:16px 18px; border-bottom:1px solid #e7ecef; background:linear-gradient(135deg,#f5fafb,#fff); }
      h2 { margin:0; font-size:1rem; } .eyebrow { margin:0 0 2px; color:#61737c; font-size:.75rem; text-transform:uppercase; letter-spacing:.08em; }
      .messages { display:flex; flex-direction:column; gap:10px; min-height:180px; max-height:420px; overflow:auto; padding:16px; background:#fbfcfc; }
      .message { max-width:86%; padding:10px 12px; border-radius:12px; white-space:pre-wrap; overflow-wrap:anywhere; }
      .message.user { align-self:flex-end; background:#1e5963; color:#fff; border-bottom-right-radius:4px; } .message.assistant { align-self:flex-start; background:#edf3f4; border-bottom-left-radius:4px; }
      .quote { margin:0 16px 12px; padding:12px; border:1px solid #bdd8dc; border-radius:12px; background:#f1fafb; } .quote strong { display:block; }
      .quote small { display:block; color:#5f7078; margin-top:3px; }
      .quote-details { display:grid; gap:4px; margin-top:9px; }
      .quote-row { display:grid; grid-template-columns:minmax(82px,max-content) 1fr; gap:8px; }
      .quote-row span:first-child { color:#5f7078; }
      .checkout { margin:0 16px 12px; padding:12px; border:1px solid #b9cfe0; border-radius:12px; background:#eef8ff; display:grid; gap:7px; }
      .checkout strong, .checkout small { display:block; } .checkout small { color:#536873; }
      .checkout a { display:inline-block; width:max-content; max-width:100%; padding:9px 12px; border-radius:9px; color:#fff; background:#1e5963; font-weight:700; text-decoration:none; }
      .checkout button { width:max-content; padding:7px 10px; background:#dcecef; color:#1e5963; } .checkout.failed { border-color:#e8b7b1; background:#fff5f4; color:#8d2018; }
      form { display:flex; gap:8px; padding:12px 16px 16px; border-top:1px solid #e7ecef; } textarea { flex:1; min-height:42px; max-height:120px; resize:vertical; border:1px solid #c8d4d8; border-radius:10px; padding:10px; font:inherit; }
      button { border:0; border-radius:10px; padding:10px 14px; font:inherit; cursor:pointer; } button.primary { background:#1e5963; color:#fff; } button.secondary { background:#e8f1f2; color:#1e5963; } button:disabled { opacity:.55; cursor:not-allowed; }
      .status { display:flex; align-items:center; gap:8px; min-height:1.3em; padding:0 16px 10px; color:#63747b; font-size:.85rem; }
      .status.busy::before { content:""; width:12px; height:12px; flex:0 0 12px; border:2px solid #c8d4d8; border-top-color:#1e5963; border-radius:50%; animation:kuroo-concierge-spin .8s linear infinite; }
      @keyframes kuroo-concierge-spin { to { transform:rotate(360deg); } }
      .actions { display:flex; gap:8px; padding:0 16px 12px; }
      @media (prefers-reduced-motion: reduce) { .status.busy::before { animation:none; } }
      .customer-form { display:grid; gap:8px; margin-top:12px; padding-top:12px; border-top:1px solid #cfe3e5; }
      .customer-form label { display:grid; gap:3px; color:#536873; font-size:.82rem; }
      .customer-form input { width:100%; box-sizing:border-box; border:1px solid #c8d4d8; border-radius:8px; padding:8px; font:inherit; color:#26343b; background:#fff; }
      .customer-form .customer-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
      .proposal-occurrences { display:grid; gap:8px; margin-top:10px; }
      .proposal-occurrence { padding:8px; border:1px solid #d5e5e7; border-radius:8px; background:#fff; }
      .proposal-occurrence.excluded, .proposal-occurrence.unavailable { opacity:.72; }
      .quote-lines { display:grid; gap:2px; margin-top:8px; color:#536873; font-size:.84rem; }
      @media (max-width:540px) { form { flex-direction:column; } .message { max-width:95%; } .actions { flex-wrap:wrap; } .customer-form .customer-grid { grid-template-columns:1fr; } }
    </style>
    <section class="shell" data-theme="${theme === "kuroo" ? "kuroo" : "default"}" aria-label="Concierge de ${PRODUCT_NAME}">
      <header><div><p class="eyebrow">${PRODUCT_NAME}</p><h2>¿En qué te ayudo?</h2></div><span aria-hidden="true">✦</span></header>
      <div class="messages" data-messages role="log" aria-live="polite"></div>
      <div class="quote" data-quote hidden></div>
      <div class="checkout" data-checkout hidden></div>
      <div class="actions"><button type="button" class="secondary" data-confirm hidden>Confirmar propuesta</button></div>
      <div class="status" data-status role="status">Podés preguntar por servicios, horarios o entradas.</div>
      <form data-form><textarea data-input aria-label="Mensaje" placeholder="${inputPlaceholder}" maxlength="1200"></textarea><button class="primary" type="submit">Enviar</button></form>
    </section>`;
    const form = this.root.querySelector("[data-form]") as HTMLFormElement | null;
    const input = this.root.querySelector("[data-input]") as HTMLTextAreaElement | null;
    form?.addEventListener("submit", (event) => {
      event.preventDefault();
      const text = input?.value.trim() ?? "";
      if (input) input.value = "";
      if (text) void this.send(text);
    });
    input?.addEventListener("keydown", (event) => {
      if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
        event.preventDefault();
        form?.requestSubmit();
      }
    });
    this.root.querySelector("[data-confirm]")?.addEventListener("click", () => {
      if (!this.pendingQuote || !this.proposalIsConfirmable()) return;
      const firstOccurrence = this.quoteOccurrences(this.pendingQuote)[0];
      void this.sendAction({
        type: "confirm-proposal",
        proposalId: this.pendingQuote.proposalId ?? this.pendingQuote.id,
        customer: {
          firstName: this.customer.firstName.trim(),
          email: this.customer.email.trim(),
          ...(this.customer.lastName?.trim() ? { lastName: this.customer.lastName.trim() } : {}),
          ...(this.customer.phone?.trim() ? { phone: this.customer.phone.trim() } : {}),
          ...(this.pendingQuote.consentRequired ? { consent: this.customer.consent === true } : {})
        },
        ...(firstOccurrence?.adjusted || this.pendingQuote.adjusted ? { adjustmentAccepted: firstOccurrence?.adjustmentAccepted === true || this.pendingQuote.adjustmentAccepted === true } : {}),
        ...(this.selectedPaymentMethod() ? { paymentMethod: this.selectedPaymentMethod() } : {})
      });
    });
    this.renderMessages();
    this.renderQuote();
    this.renderCheckout();
  }

  private setStatus(message: string, error = false, busy = false): void {
    const status = this.root.querySelector("[data-status]");
    if (status) {
      status.textContent = message;
      status.setAttribute("aria-live", error ? "assertive" : "polite");
      status.classList.toggle("busy", busy);
      status.setAttribute("aria-busy", busy ? "true" : "false");
    }
  }

  private clearInputPlaceholder(): void {
    const input = this.root.querySelector("[data-input]") as HTMLTextAreaElement | null;
    if (input) input.placeholder = "";
  }

  private renderMessages(): void {
    const container = this.root.querySelector("[data-messages]");
    if (!container) return;
    container.replaceChildren();
    for (const message of this.messages) {
      const item = document.createElement("div");
      item.className = `message ${message.role}`;
      item.textContent = textFromParts(message.parts);
      container.append(item);
    }
    if (this.activeAssistant) {
      const item = document.createElement("div");
      item.className = "message assistant";
      item.textContent = this.activeAssistant;
      container.append(item);
    }
    container.scrollTop = container.scrollHeight;
  }

  private customerIsValid(): boolean {
    return this.customer.firstName.trim().length > 0 && /^\S+@\S+\.\S+$/u.test(this.customer.email.trim());
  }

  private quoteOccurrences(quote: QuoteSnapshot): QuoteOccurrence[] {
    if (quote.occurrences?.length) return quote.occurrences;
    if (!quote.appointment) return [];
    return [{
      sequence: 0,
      quoteId: quote.id,
      ...quote.appointment,
      totalCents: quote.totalCents,
      currency: quote.currency,
      appliedRules: quote.appliedRules,
      adjusted: quote.adjusted,
      adjustmentReason: quote.adjustmentReason,
      ...(quote.adjustmentAccepted === undefined ? {} : { adjustmentAccepted: quote.adjustmentAccepted })
    }];
  }

  private proposalIsConfirmable(): boolean {
    const quote = this.pendingQuote;
    if (!quote || !quote.id || !quote.expiresAt || Number.isNaN(Date.parse(quote.expiresAt)) || Date.parse(quote.expiresAt) <= Date.now()) return false;
    if (!this.customerIsValid()) return false;
    const occurrences = this.quoteOccurrences(quote);
    if (occurrences.length && !occurrences.some((occurrence) => !["excluded", "cancelled", "unavailable", "expired"].includes(occurrence.status ?? ""))) return false;
    return occurrences.every((occurrence) => {
      if (["excluded", "cancelled"].includes(occurrence.status ?? "")) return true;
      if (["unavailable", "expired"].includes(occurrence.status ?? "")) return false;
      if (!occurrence.quoteId) return false;
      if ((quote.kind ?? quote.targetKind) === "service" && !occurrence.holdId) return false;
      if (occurrence.status === "adjusted" || occurrence.adjusted) return occurrence.adjustmentAccepted === true;
      return true;
    });
  }

  private updateConfirmState(): void {
    const confirm = this.root.querySelector("[data-confirm]") as HTMLButtonElement | null;
    if (confirm) confirm.disabled = Boolean(this.activeRequestId) || !this.proposalIsConfirmable();
  }

  private proposalDecisionOccurrences(): Array<{ sequence: number; include: boolean; adjustmentAccepted?: boolean }> {
    return this.quoteOccurrences(this.pendingQuote ?? { id: "", totalCents: 0, expiresAt: "" }).map((occurrence) => ({
      sequence: occurrence.sequence,
      include: !["excluded", "cancelled"].includes(occurrence.status ?? ""),
      ...(occurrence.adjusted || occurrence.adjustmentAccepted !== undefined ? { adjustmentAccepted: occurrence.adjustmentAccepted === true } : {})
    }));
  }

  private updateProposalDecision(sequence: number, change: { include?: boolean; adjustmentAccepted?: boolean }): void {
    const quote = this.pendingQuote;
    const proposalId = quote?.proposalId ?? quote?.id;
    if (!quote || !proposalId || !quote.occurrences?.length || (quote.kind ?? quote.targetKind) !== "series" || this.activeRequestId) return;
    this.pendingQuote = {
      ...quote,
      occurrences: quote.occurrences.map((occurrence) => {
        if (occurrence.sequence !== sequence) return occurrence;
        const nextStatus = change.include === false ? "excluded" : change.include === true && ["excluded", "cancelled"].includes(occurrence.status ?? "") ? "proposed" : occurrence.status;
        return { ...occurrence, ...(nextStatus ? { status: nextStatus } : {}), ...(change.adjustmentAccepted === undefined ? {} : { adjustmentAccepted: change.adjustmentAccepted }) };
      })
    };
    this.renderQuote();
    void this.sendAction({ type: "update-proposal", proposalId, occurrences: this.proposalDecisionOccurrences() });
  }

  private setSimpleAdjustmentDecision(accepted: boolean): void {
    if (!this.pendingQuote || (this.pendingQuote.kind ?? this.pendingQuote.targetKind) === "series") return;
    this.pendingQuote = { ...this.pendingQuote, adjustmentAccepted: accepted, occurrences: this.pendingQuote.occurrences?.map((occurrence) => ({ ...occurrence, ...(occurrence.adjusted ? { adjustmentAccepted: accepted } : {}) })) };
    this.updateConfirmState();
  }

  private appendQuoteRow(container: HTMLElement, label: string, value: string | undefined): void {
    if (!value) return;
    const row = document.createElement("div");
    row.className = "quote-row";
    const name = document.createElement("span");
    name.textContent = label;
    const content = document.createElement("span");
    content.textContent = value;
    row.append(name, content);
    container.append(row);
  }

  private appendQuoteLines(card: HTMLElement, lines: unknown[] | undefined, currency: string | undefined): void {
    if (!lines?.length) return;
    const list = document.createElement("div");
    list.className = "quote-lines";
    for (const raw of lines) {
      const line = objectRecord(raw);
      if (!line) continue;
      const label = firstString([line], "label", "name") ?? "Concepto";
      const total = firstNumber([line], "totalCents", "total_cents");
      const quantity = firstNumber([line], "quantity", "qty");
      const item = document.createElement("div");
      item.textContent = `${quantity && quantity > 1 ? `${quantity} × ` : ""}${label}${total !== undefined ? `: ${money(total, currency, this.currentLocale())}` : ""}`;
      list.append(item);
    }
    if (list.childElementCount) card.append(list);
  }

  private renderOccurrence(container: HTMLElement, occurrence: QuoteOccurrence, totalOccurrences: number): void {
    const item = document.createElement("div");
    item.className = `proposal-occurrence ${occurrence.status ?? ""}`;
    const title = document.createElement("strong");
    title.textContent = totalOccurrences > 1 ? `Cita ${occurrence.sequence + 1}` : "Detalle de la cita";
    item.append(title);
    const details = document.createElement("div");
    details.className = "quote-details";
    this.appendQuoteRow(details, "Servicio", occurrence.serviceName);
    this.appendQuoteRow(details, "Fecha", formatAppointmentRange(occurrence, this.currentLocale()));
    this.appendQuoteRow(details, "Profesional", occurrence.providerName);
    this.appendQuoteRow(details, "Ubicación", occurrence.locationName ?? (occurrence.locationId ? undefined : "Videollamada"));
    this.appendQuoteRow(details, "Duración", occurrence.durationMinutes ? `${occurrence.durationMinutes} min` : undefined);
    this.appendQuoteRow(details, "Personas", occurrence.persons ? String(occurrence.persons) : undefined);
    if (occurrence.status === "unavailable") this.appendQuoteRow(details, "Estado", "No disponible; elegí otra opción o excluí esta fecha.");
    if (occurrence.adjusted) this.appendQuoteRow(details, "Ajuste", occurrence.adjustmentReason ?? "Ajuste aplicado por Kuroo; requiere aceptación.");
    if (occurrence.totalCents !== undefined) this.appendQuoteRow(details, "Importe", money(occurrence.totalCents, occurrence.currency ?? this.pendingQuote?.currency, this.currentLocale()));
    if (details.childElementCount) item.append(details);
    this.appendQuoteLines(item, occurrence.lines, occurrence.currency ?? this.pendingQuote?.currency);
    if (totalOccurrences > 1 && ["unavailable", "excluded", "cancelled"].includes(occurrence.status ?? "")) {
      const decision = document.createElement("label");
      const include = document.createElement("input");
      include.type = "checkbox";
      include.checked = !["excluded", "cancelled"].includes(occurrence.status ?? "");
      include.disabled = Boolean(this.activeRequestId);
      include.addEventListener("change", () => this.updateProposalDecision(occurrence.sequence, { include: include.checked }));
      decision.append(include, document.createTextNode(" Incluir esta fecha en la serie"));
      item.append(decision);
    }
    if (occurrence.adjusted) {
      const decision = document.createElement("label");
      const accepted = document.createElement("input");
      accepted.type = "checkbox";
      accepted.checked = occurrence.adjustmentAccepted === true;
      accepted.disabled = Boolean(this.activeRequestId);
      accepted.addEventListener("change", () => totalOccurrences > 1
        ? this.updateProposalDecision(occurrence.sequence, { adjustmentAccepted: accepted.checked })
        : this.setSimpleAdjustmentDecision(accepted.checked));
      decision.append(accepted, document.createTextNode(" Acepto el ajuste indicado por Kuroo"));
      item.append(decision);
    }
    container.append(item);
  }

  private renderCustomerForm(card: HTMLElement): void {
    const form = document.createElement("div");
    form.className = "customer-form";
    const heading = document.createElement("strong");
    heading.textContent = "Datos para reservar";
    form.append(heading);
    const grid = document.createElement("div");
    grid.className = "customer-grid";
    const addInput = (key: "firstName" | "email" | "lastName" | "phone", labelText: string, type: string, required: boolean): void => {
      const label = document.createElement("label");
      label.textContent = `${labelText}${required ? " *" : ""}`;
      const input = document.createElement("input");
      input.type = type;
      input.required = required;
      input.value = this.customer[key] ?? "";
      input.autocomplete = key === "firstName" ? "given-name" : key === "lastName" ? "family-name" : key === "email" ? "email" : "tel";
      input.addEventListener("input", () => {
        this.customer = { ...this.customer, [key]: input.value };
        this.updateConfirmState();
      });
      label.append(input);
      grid.append(label);
    };
    addInput("firstName", "Nombre", "text", true);
    addInput("email", "Email", "email", true);
    addInput("lastName", "Apellido", "text", false);
    addInput("phone", "Teléfono", "tel", false);
    form.append(grid);
    if (this.pendingQuote?.consentRequired) {
      const consentLabel = document.createElement("label");
      const consent = document.createElement("input");
      consent.type = "checkbox";
      consent.checked = this.customer.consent === true;
      consent.addEventListener("change", () => {
        this.customer = { ...this.customer, consent: consent.checked };
        this.updateConfirmState();
      });
      consentLabel.append(consent, document.createTextNode(" Acepto el tratamiento de mis datos para gestionar la reserva."));
      form.append(consentLabel);
    }
    card.append(form);
  }

  private mergeProposalDecisions(quote: QuoteSnapshot): QuoteSnapshot {
    const previousBySequence = new Map((this.pendingQuote?.occurrences ?? []).map((occurrence) => [occurrence.sequence, occurrence]));
    if (!quote.occurrences?.length || !previousBySequence.size) return quote;
    return {
      ...quote,
      occurrences: quote.occurrences.map((occurrence) => {
        const previous = previousBySequence.get(occurrence.sequence);
        return previous && occurrence.adjustmentAccepted === undefined && previous.adjustmentAccepted !== undefined
          ? { ...occurrence, adjustmentAccepted: previous.adjustmentAccepted }
          : occurrence;
      })
    };
  }

  private renderQuote(): void {
    const card = this.root.querySelector("[data-quote]") as HTMLElement | null;
    const confirm = this.root.querySelector("[data-confirm]") as HTMLButtonElement | null;
    if (!card || !confirm) return;
    if (!this.pendingQuote) { card.hidden = true; confirm.hidden = true; confirm.disabled = true; return; }
    const quote = this.pendingQuote;
    card.hidden = false;
    card.replaceChildren();
    const strong = document.createElement("strong");
    strong.textContent = `Propuesta: ${money(quote.totalCents, quote.currency, this.currentLocale())}`;
    const small = document.createElement("small");
    const expiry = Date.parse(quote.expiresAt);
    small.textContent = Number.isFinite(expiry) ? `Válida hasta ${new Date(expiry).toLocaleString(this.currentLocale())}.` : "Propuesta calculada por Kuroo.";
    card.append(strong, small);
    const occurrences = this.quoteOccurrences(quote);
    if (occurrences.length) {
      const list = document.createElement("div");
      list.className = "proposal-occurrences";
      for (const occurrence of occurrences) this.renderOccurrence(list, occurrence, occurrences.length);
      card.append(list);
    }
    this.appendQuoteLines(card, quote.lines, quote.currency);
    const ruleText = quote.appliedRules?.map((rule) => rule.reason ?? rule.label).filter((value): value is string => Boolean(value)).join(" · ");
    if (ruleText) {
      const rules = document.createElement("small");
      rules.textContent = `Ajustes de Kuroo: ${ruleText}`;
      card.append(rules);
    }
    this.renderCustomerForm(card);
    confirm.hidden = false;
    this.updateConfirmState();
    this.dispatchEvent(new CustomEvent("kuroo:quote", { bubbles: true, composed: true, detail: this.pendingQuote }));
  }

  private paymentStatusUrl(paymentId: string): string | undefined {
    const base = this.getAttribute("api-base-url")?.trim();
    if (!base) return undefined;
    const normalized = base.replace(/\/$/u, "");
    const api = normalized.endsWith("/api/v1") ? normalized : `${normalized}/api/v1`;
    return `${api}/payments/${encodeURIComponent(paymentId)}/status`;
  }

  private paymentStatusLabel(status: string | undefined): string {
    switch (status) {
      case "paid": return "Pago aprobado; Kuroo confirmará la reserva mediante el webhook del proveedor.";
      case "failed": return "El pago fue rechazado. Podés reintentar desde el checkout.";
      case "cancelled":
      case "expired": return "El checkout expiró o fue cancelado.";
      case "authorized": return "Pago autorizado; esperando la confirmación del proveedor.";
      default: return "Pago pendiente; esta tarjeta se actualizará automáticamente.";
    }
  }

  private renderCheckout(): void {
    const card = this.root.querySelector("[data-checkout]") as HTMLElement | null;
    if (!card) return;
    const checkout = this.pendingCheckout;
    if (!checkout) { card.hidden = true; return; }
    card.hidden = false;
    card.classList.toggle("failed", ["failed", "cancelled", "expired"].includes(checkout.status ?? ""));
    card.replaceChildren();
    const strong = document.createElement("strong");
    strong.textContent = checkout.bookingStatus === "confirmed" || checkout.eventOrderStatus === "confirmed" ? "Reserva confirmada" : checkout.status === "paid" ? "Pago aprobado" : "Checkout seguro";
    const small = document.createElement("small");
    small.textContent = checkout.bookingStatus === "confirmed" || checkout.eventOrderStatus === "confirmed" ? "La reserva quedó confirmada en Kuroo." : this.paymentStatusLabel(checkout.status);
    card.append(strong, small);
    if (checkout.checkoutUrl) {
      const link = document.createElement("a");
      link.href = checkout.checkoutUrl;
      link.target = "_blank";
      link.rel = "noreferrer noopener";
      link.textContent = "Pagar ahora en checkout seguro";
      card.append(link);
    }
    if (checkout.paymentId && !["paid", "failed", "cancelled", "expired"].includes(checkout.status ?? "")) {
      const button = document.createElement("button");
      button.type = "button";
      button.textContent = "Consultar estado ahora";
      button.disabled = Boolean(this.activeRequestId);
      button.addEventListener("click", () => void this.refreshCheckoutStatus(true));
      card.append(button);
    }
  }

  private startCheckoutPolling(): void {
    if (!this.pendingCheckout?.paymentId || !this.paymentStatusUrl(this.pendingCheckout.paymentId)) return;
    if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
    this.paymentPollAttempts = 0;
    this.paymentPollTimer = window.setInterval(() => void this.refreshCheckoutStatus(), 5_000);
    void this.refreshCheckoutStatus();
  }

  private async refreshCheckoutStatus(manual = false): Promise<void> {
    const paymentId = this.pendingCheckout?.paymentId;
    const url = paymentId ? this.paymentStatusUrl(paymentId) : undefined;
    if (!url || (this.paymentPollTimer === undefined && !manual)) return;
    if (this.paymentPollAttempts >= 24) {
      if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
      this.paymentPollTimer = undefined;
      return;
    }
    this.paymentPollAttempts += 1;
    try {
      const response = await fetch(url, { credentials: "include", headers: { accept: "application/json" } });
      const payload = await response.json().catch(() => undefined) as Record<string, unknown> | undefined;
      const data = payload && typeof payload.data === "object" && payload.data !== null ? payload.data as Record<string, unknown> : payload;
      if (!response.ok || !data) throw new Error("PAYMENT_STATUS_FAILED");
      this.pendingCheckout = {
        ...this.pendingCheckout,
        status: typeof data.status === "string" ? data.status : this.pendingCheckout?.status,
        statusDetail: typeof data.statusDetail === "string" ? data.statusDetail : this.pendingCheckout?.statusDetail,
        bookingStatus: typeof data.bookingStatus === "string" ? data.bookingStatus : this.pendingCheckout?.bookingStatus,
        eventOrderStatus: typeof data.eventOrderStatus === "string" ? data.eventOrderStatus : this.pendingCheckout?.eventOrderStatus
      };
      this.renderCheckout();
      this.dispatchEvent(new CustomEvent("kuroo:payment-status", { bubbles: true, composed: true, detail: this.pendingCheckout }));
      if (["paid", "failed", "cancelled", "expired"].includes(this.pendingCheckout.status ?? "") || this.pendingCheckout.bookingStatus === "confirmed" || this.pendingCheckout.eventOrderStatus === "confirmed") {
        if (this.paymentPollTimer) window.clearInterval(this.paymentPollTimer);
        this.paymentPollTimer = undefined;
      }
    } catch {
      if (manual) this.setStatus("No pudimos consultar el estado del pago. Reintentá en unos segundos.", true);
    }
  }

  private scheduleReconnect(): void {
    if (!this.isConnected || !this.connectionWanted || this.reconnectTimer !== undefined) return;
    const attempt = this.reconnectAttempts;
    this.reconnectAttempts += 1;
    const delay = Math.min(10_000, 1_000 * (2 ** Math.min(attempt, 4)));
    this.setStatus(this.activeRequestId ? "Reconectando y retomando la operación…" : "Reconectando…", false, true);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = undefined;
      this.connect();
    }, delay);
  }

  private connect(): void {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) return;
    const url = this.agentUrl();
    if (!url) { this.setStatus("El concierge no está configurado temporalmente. Reintentá más tarde.", true); return; }
    try {
      this.socket = new WebSocket(url);
      this.socket.addEventListener("open", () => {
        this.reconnectAttempts = 0;
        if (this.activeRequestId) {
          this.setStatus("Retomando la operación…", false, true);
          const probeId = randomId("resume");
          this.resumeProbeId = probeId;
          this.socket?.send(JSON.stringify({ type: "cf_agent_stream_resume_request", probeId }));
          return;
        }
        this.setStatus("Listo para ayudarte.");
        const queuedText = this.queuedText;
        const queuedAction = this.queuedAction;
        this.queuedText = undefined;
        this.queuedAction = undefined;
        if (queuedAction) void this.sendAction(queuedAction);
        else if (queuedText) void this.send(queuedText);
      });
      this.socket.addEventListener("message", (event) => this.receive(event.data));
      this.socket.addEventListener("error", () => {
        this.setStatus("No pudimos conectar el concierge. Reintentá en unos segundos.", true);
        this.dispatchEvent(new CustomEvent("kuroo:error", { bubbles: true, composed: true, detail: { stage: "connection" } }));
      });
      this.socket.addEventListener("close", () => {
        this.socket = undefined;
        this.resumeAckedRequestIds.clear();
        if (this.activeRequestId) {
          this.setStatus("La conexión se interrumpió; podés reintentar sin perder la propuesta.", true);
        }
        this.scheduleReconnect();
      });
    } catch {
      this.setStatus("No pudimos iniciar el concierge. Reintentá en unos segundos.", true);
      this.scheduleReconnect();
    }
  }

  private async send(text: string): Promise<void> {
    this.connectionWanted = true;
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      this.queuedText = text;
      this.queuedAction = undefined;
      this.clearInputPlaceholder();
      this.setStatus("Conectando…", false, true);
      this.connect();
      return;
    }
    const user: ChatMessage = { id: randomId("message"), role: "user", parts: [{ type: "text", text }] };
    this.messages.push(user);
    this.dispatchEvent(new CustomEvent("kuroo:message", { bubbles: true, composed: true, detail: { role: "user", text } }));
    await this.sendRequest();
  }

  private async sendAction(action: ConciergeAction): Promise<void> {
    this.connectionWanted = true;
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      this.queuedAction = action;
      this.queuedText = undefined;
      this.setStatus("Conectando…", false, true);
      this.connect();
      return;
    }
    await this.sendRequest(action);
  }

  private async sendRequest(action?: ConciergeAction): Promise<void> {
    const socket = this.socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    this.clearInputPlaceholder();
    this.activeAssistant = "";
    this.activeRequestId = randomId("request");
    this.renderMessages();
    this.renderQuote();
    this.setStatus(action ? "Creando checkout seguro…" : "Pensando…", false, true);
    const body = JSON.stringify({
      messages: this.messages,
      ...this.context(),
      ...(action ? { kurooAction: action } : {})
    });
    socket.send(JSON.stringify({ id: this.activeRequestId, init: { method: "POST", body }, type: "cf_agent_use_chat_request" }));
  }

  private consumeUiEvent(value: unknown): void {
    if (!value || typeof value !== "object") return;
    const record = value as Record<string, unknown>;
    const structuredType = typeof record.type === "string" ? record.type : undefined;
    const payload = record.payload && typeof record.payload === "object" && !Array.isArray(record.payload) ? record.payload : value;
    if (structuredType === "quote") {
      const quote = quoteFromValue(payload);
      if (quote) { this.pendingQuote = this.mergeProposalDecisions(quote); this.renderQuote(); }
      return;
    }
    if (structuredType === "hold") {
      const hold = payload as Record<string, unknown>;
      if (typeof hold.expiresAt === "string") {
        this.holdExpiresAt = hold.expiresAt;
        const quoteId = typeof hold.quoteId === "string" ? hold.quoteId : undefined;
        const sequence = typeof hold.sequence === "number" ? hold.sequence : undefined;
        if (this.pendingQuote) {
          const occurrences = this.quoteOccurrences(this.pendingQuote);
          this.pendingQuote = {
            ...this.pendingQuote,
            occurrences: occurrences.map((occurrence) => occurrence.sequence === sequence || (quoteId && (occurrence.quoteId === quoteId || this.pendingQuote?.id === quoteId))
              ? { ...occurrence, holdId: typeof hold.holdId === "string" ? hold.holdId : occurrence.holdId, status: "held" }
              : occurrence)
          };
        }
        this.renderQuote();
        this.dispatchEvent(new CustomEvent("kuroo:hold", { bubbles: true, composed: true, detail: hold }));
      }
      return;
    }
    if (structuredType === "checkout") {
      const checkout = checkoutFromValue(value);
      if (checkout) {
        this.pendingCheckout = { ...this.pendingCheckout, ...checkout };
        this.renderCheckout();
        this.dispatchEvent(new CustomEvent("kuroo:checkout", { bubbles: true, composed: true, detail: this.pendingCheckout }));
        this.startCheckoutPolling();
      }
      return;
    }
    if (structuredType === "payment_status") {
      const status = payload as Record<string, unknown>;
      if (this.pendingCheckout && typeof status.status === "string") {
        this.pendingCheckout = { ...this.pendingCheckout, status: status.status, statusDetail: typeof status.statusDetail === "string" ? status.statusDetail : this.pendingCheckout.statusDetail };
        this.renderCheckout();
      }
      return;
    }
    if (structuredType === "fallback") {
      this.setStatus("La reserva necesita una decisión adicional. Revisá la propuesta y respondé para continuar.", true);
      return;
    }
    const event = eventFromValue(value);
    if (!event) return;
    if (event.kind === "quote") {
      this.pendingQuote = this.mergeProposalDecisions(event.value as QuoteSnapshot);
      this.renderQuote();
    } else if (event.kind === "hold") {
      this.holdExpiresAt = String((event.value as Record<string, unknown>).expiresAt);
      this.dispatchEvent(new CustomEvent("kuroo:hold", { bubbles: true, composed: true, detail: event.value }));
    } else {
      const checkout = checkoutFromValue(event.value);
      if (checkout) {
        this.pendingCheckout = { ...this.pendingCheckout, ...checkout };
        this.renderCheckout();
        this.dispatchEvent(new CustomEvent("kuroo:checkout", { bubbles: true, composed: true, detail: this.pendingCheckout }));
        this.startCheckoutPolling();
      }
    }
  }

  private hydrateState(value: unknown): void {
    const state = objectRecord(value);
    if (!state) return;
    const proposal = quoteFromValue(state.pendingProposal);
    if (proposal) {
      this.pendingQuote = proposal;
      this.renderQuote();
    }
    const checkout = checkoutFromValue(state.pendingCheckout);
    if (checkout) {
      this.pendingCheckout = { ...this.pendingCheckout, ...checkout };
      this.renderCheckout();
      if (checkout.paymentId) this.startCheckoutPolling();
    }
  }

  private receive(raw: unknown): void {
    let frame: Record<string, unknown>;
    try { frame = JSON.parse(String(raw)) as Record<string, unknown>; } catch { return; }
    if (frame.type === "cf_agent_stream_resuming" && typeof frame.id === "string") {
      if (this.activeRequestId && frame.id !== this.activeRequestId) return;
      if (this.resumeAckedRequestIds.has(frame.id)) return;
      this.resumeAckedRequestIds.add(frame.id);
      this.activeRequestId = frame.id;
      this.activeAssistant = "";
      this.resumeProbeId = undefined;
      this.socket?.send(JSON.stringify({ type: "cf_agent_stream_resume_ack", id: frame.id }));
      this.setStatus("Retomando la operación…", false, true);
      return;
    }
    if (frame.type === "cf_agent_stream_resume_none") {
      if (frame.probeId && frame.probeId !== this.resumeProbeId) return;
      if (this.activeRequestId) this.resumeAckedRequestIds.delete(this.activeRequestId);
      this.resumeProbeId = undefined;
      if (this.activeRequestId) {
        this.activeRequestId = undefined;
        this.activeAssistant = "";
        this.setStatus("La operación terminó mientras se recuperaba la conexión. Revisá la propuesta y reintentá si hace falta.", true);
        this.renderMessages();
        this.renderQuote();
        this.renderCheckout();
      }
      return;
    }
    if (frame.type === "cf_agent_stream_pending" || frame.type === "cf_agent_chat_recovering") {
      if (!this.activeRequestId || !frame.id || frame.id === this.activeRequestId) this.setStatus("La operación sigue activa; esperando la respuesta…", false, true);
      return;
    }
    if (frame.type === "kuroo_concierge_status") {
      if (frame.requestId === this.activeRequestId) {
        const phase = typeof frame.phase === "string" ? frame.phase : "processing";
        const elapsed = typeof frame.elapsedMs === "number" ? Math.round(frame.elapsedMs / 1000) : 0;
        const label = phase === "checking_availability" ? "Consultando disponibilidad…" : phase === "calculating_proposal" ? "Calculando propuesta…" : phase === "creating_checkout" ? "Creando checkout seguro…" : phase === "still_working" ? `La operación sigue activa (${elapsed}s)…` : "Pensando…";
        this.setStatus(label, false, true);
      }
      return;
    }
    if (frame.type === "kuroo_concierge_state" || frame.type === "cf_agent_state") {
      this.hydrateState(frame.state ?? frame.data);
      return;
    }
    if (frame.type === "cf_agent_chat_messages" && Array.isArray(frame.messages)) {
      const hydrated = frame.messages.filter((message): message is ChatMessage => Boolean(message) && typeof message === "object" && ((message as Record<string, unknown>).role === "user" || (message as Record<string, unknown>).role === "assistant"))
        .map((message) => ({ id: String(message.id ?? randomId("message")), role: message.role, parts: [{ type: "text" as const, text: textFromParts(message.parts) }] }));
      if (hydrated.length) { this.messages = hydrated; this.clearInputPlaceholder(); this.renderMessages(); }
      return;
    }
    if (frame.type !== "cf_agent_use_chat_response" || frame.id !== this.activeRequestId) return;
    if (frame.error) {
      this.setStatus(typeof frame.body === "string" ? frame.body : "No se pudo completar la consulta. Reintentá en unos segundos.", true);
      this.activeRequestId = undefined;
      this.renderQuote();
      return;
    }
    if (typeof frame.body === "string" && frame.body.trim()) {
      let chunk: unknown;
      try { chunk = JSON.parse(frame.body); } catch { chunk = undefined; }
      if (chunk && typeof chunk === "object") {
        const record = chunk as Record<string, unknown>;
        const delta = typeof record.delta === "string" ? record.delta : typeof record.textDelta === "string" ? record.textDelta : undefined;
        if (delta) this.activeAssistant += delta;
        if (record.type === "text" && typeof record.text === "string") this.activeAssistant += record.text;
        if (record.type === "start" || record.type === "text-start") this.activeAssistant = "";
        const metadata = record.type === "message-metadata" && record.messageMetadata && typeof record.messageMetadata === "object" ? record.messageMetadata as Record<string, unknown> : undefined;
        if (Array.isArray(metadata?.uiEvents)) for (const event of metadata.uiEvents) this.consumeUiEvent(event);
        if (record.type !== "message-metadata") this.consumeUiEvent(chunk);
        this.renderMessages();
      }
    }
    if (frame.done) {
      if (this.activeAssistant) {
        const assistant: ChatMessage = { id: randomId("assistant"), role: "assistant", parts: [{ type: "text", text: this.activeAssistant }] };
        this.messages.push(assistant);
        this.dispatchEvent(new CustomEvent("kuroo:message", { bubbles: true, composed: true, detail: assistant }));
      }
      this.activeAssistant = "";
      if (typeof frame.id === "string") this.resumeAckedRequestIds.delete(frame.id);
      this.activeRequestId = undefined;
      this.setStatus(this.holdExpiresAt ? `El hold vence a las ${new Date(this.holdExpiresAt).toLocaleTimeString(this.currentLocale())}.` : "¿Querés que busque otra opción?");
      this.renderMessages();
      this.renderQuote();
      this.renderCheckout();
    }
  }
}

export function defineKurooConcierge(): void {
  if (!customElements.get(elementName)) customElements.define(elementName, KurooConciergeElement);
}

defineKurooConcierge();
